import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UsersIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { LLENC } from '../tema';
import { textRic } from '../textRic';

// LA PANTALLA DE LA MODELITZACIÓ BASADA EN AGENTS.
//
// **No és un «coming soon».** Un «coming soon» és una promesa buida i aquesta aplicació ja n'ha
// tingut prou —tres pantalles amb dades inventades i quatre etapes del SSM que deien «encara
// no»—. Això és **una nota perquè no es perdi**: què és l'ABM, què aportaria que els altres dos
// llenços no poden, per què encara no hi és, i com es faria el dia que es faci.
//
// Es queda a la vista a posta. És la peça que falta per poder ensenyar **l'emergència**, i
// oblidar-se-la seria pitjor que no tenir-la.

// El dibuix. Quatre agents amb una regla local i el que en surt: **la mateixa regla, dues
// vegades, i la segona ja no és un desordre**. És Schelling en petit, i és tot l'argument.
function Emergencia() {
  const { t } = useTranslation();
  const N = 8, C = 13;
  // Abans: barrejats. Després: agrupats. Ni una cosa ni l'altra no és aleatòria — són dos estats
  // fixos, perquè el dibuix ha de dir sempre el mateix.
  const abans = '10110100 01001011 10110010 01101101 10010110 01101001 10110100 01001011';
  const despres = '11110000 11110000 11110000 11100000 00011111 00011111 00001111 00001111';
  const graella = (patro, x0) => patro.replace(/ /g, '').split('').map((v, i) => (
    <rect key={i} x={x0 + (i % N) * C} y={Math.floor(i / N) * C} width={C - 2} height={C - 2} rx="2"
      fill={v === '1' ? LLENC.estoc.vora : LLENC.flux.vora} opacity={v === '1' ? 0.85 : 0.7} />
  ));
  return (
    <figure className="mt-2">
      <svg viewBox="0 0 250 110" className="w-full" style={{ maxHeight: 170 }} role="img"
        aria-label={t('abm.figAria')}>
        {graella(abans, 0)}
        {graella(despres, 146)}
        <g transform="translate(112,52)">
          <line x1="0" y1="0" x2="20" y2="0" stroke={LLENC.textPal} strokeWidth="1.5" />
          <polygon points="0,0 -6,-3 -6,3" fill={LLENC.textPal} transform="translate(20,0)" />
        </g>
        <text x="0" y="108" fontSize="8" fill={LLENC.textPal}>{t('abm.figAbans')}</text>
        <text x="146" y="108" fontSize="8" fill={LLENC.textPal}>{t('abm.figDespres')}</text>
      </svg>
      <figcaption className="text-xs text-gray-500 mt-1.5">{t('abm.figPeu')}</figcaption>
    </figure>
  );
}

const P = ({ clau }) => {
  const { t } = useTranslation();
  return (
    <p className="text-sm text-gray-700 leading-relaxed"
       dangerouslySetInnerHTML={{ __html: textRic(t(clau)) }} />
  );
};

export default function EncaraNoABM() {
  const { t } = useTranslation();
  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <header>
          <UsersIcon className="h-8 w-8 text-gray-400 mb-3" />
          <h1 className="text-2xl font-bold text-gray-900">{t('abm.abmTitol')}</h1>
        </header>

        <section className="bg-white border border-gray-200 rounded-md p-6 space-y-3">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            {t('abm.abmQue')}
          </h2>
          <P clau="abm.abmP1" />
          <P clau="abm.abmP2" />
          <P clau="abm.abmP3" />
          <Emergencia />
        </section>

        <section className="bg-white border border-gray-200 rounded-md p-6 space-y-3">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            {t('abm.abmPerQue')}
          </h2>
          <P clau="abm.abmP4" />
          <P clau="abm.abmP5" />
        </section>

        <section className="bg-white border border-gray-200 rounded-md p-6 space-y-3">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            {t('abm.abmCom')}
          </h2>
          <P clau="abm.abmP6" />
          <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3 leading-relaxed"
             dangerouslySetInnerHTML={{ __html: textRic(t('abm.abmNota')) }} />
        </section>

        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
            {t('abm.abmMentre')}
          </h2>
          <p className="text-sm text-gray-700 mb-4">{t('abm.abmP7')}</p>
          <Link to="/models"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary-700 text-white
                       rounded-md text-sm font-semibold hover:bg-primary-800">
            {t('abm.aModels')}<ArrowRightIcon className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </div>
  );
}

// Com a `AjudaEtapa`: `**això**` passa a `<strong>` i la resta s'escapa.

